import axios from "axios";
import type { AuthErrorCode } from "@/domain/types";
import { CONFIG } from "@/config";

const AUTH_BASE_URL = CONFIG.AUTH_URL;
const APP_ID = "NEXO_WAREHOUSE";
const TOKEN_KEY = "nexo-auth-token";

export function createAuthError(code: AuthErrorCode): Error {
  const err = new Error(code);
  err.name = "AuthError";
  return err;
}

export function isAuthError(err: unknown): err is Error & { message: AuthErrorCode } {
  return err instanceof Error && err.name === "AuthError";
}

export async function signIn(identifier: string, password: string): Promise<string> {
  try {
    const response = await axios.post<{ token: string }>(
      `${AUTH_BASE_URL}/v1/auth/signin`,
      { identifier, password, appId: APP_ID },
      { headers: { "Content-Type": "application/json" }, timeout: 15_000 }
    );

    const { token } = response.data;
    saveToken(token);
    return token;
  } catch (err) {
    if (!axios.isAxiosError(err)) throw createAuthError("UNKNOWN_ERROR");
    if (err.code === "ECONNABORTED" || err.code === "ERR_NETWORK") throw createAuthError("NETWORK_ERROR");
    if (err.response?.status === 403 && err.response.data?.code === "FORBIDDEN_ERROR") {
      throw createAuthError("SUBSCRIPTION_EXPIRED");
    }
    if (err.response) throw createAuthError("AUTH_ERROR");
    throw createAuthError("NETWORK_ERROR");
  }
}

export async function refreshToken(): Promise<string> {
  const currentToken = getToken();
  if (!currentToken) throw createAuthError("AUTH_ERROR");

  try {
    const response = await axios.post<{ token: string }>(
      `${AUTH_BASE_URL}/v1/auth/refresh`,
      { appId: APP_ID },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentToken}`,
        },
        timeout: 10_000,
      }
    );

    const { token } = response.data;
    saveToken(token);
    return token;
  } catch (err) {
    if (!axios.isAxiosError(err)) throw createAuthError("UNKNOWN_ERROR");
    if (err.response?.status === 403 && err.response.data?.code === "FORBIDDEN_ERROR") {
      throw createAuthError("SUBSCRIPTION_EXPIRED");
    }
    throw createAuthError("AUTH_ERROR");
  }
}

export function saveToken(token: string) {
  try { sessionStorage.setItem(TOKEN_KEY, token); } catch {}
}

export function getToken(): string | null {
  try { return sessionStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function clearToken() {
  try { sessionStorage.removeItem(TOKEN_KEY); } catch {}
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

export async function switchApp(appId: string): Promise<string> {
  const currentToken = getToken();
  if (!currentToken) throw createAuthError("AUTH_ERROR");

  try {
    const response = await axios.post<{ token: string }>(
      `${AUTH_BASE_URL}/v1/auth/switch-app`,
      { appId },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentToken}`,
        },
        timeout: 10_000,
      }
    );
    return response.data.token;
  } catch (err) {
    if (!axios.isAxiosError(err)) throw createAuthError("UNKNOWN_ERROR");
    if (err.response?.status === 403 && err.response.data?.code === "FORBIDDEN_ERROR") {
      throw createAuthError("SUBSCRIPTION_EXPIRED");
    }
    throw createAuthError("AUTH_ERROR");
  }
}
